import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Heart } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface InterestButtonProps {
  stationId: string;
  stationTitle: string;
}

export const InterestButton = ({ stationId, stationTitle }: InterestButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const interestData = {
      stationId,
      userName: formData.get('name') as string,
      userEmail: formData.get('email') as string,
      userPhone: formData.get('phone') as string,
      message: formData.get('message') as string,
      userId: user?.id,
    };

    try {
      const { error } = await supabase.functions.invoke('submit-interest', {
        body: interestData
      });

      if (error) throw error;

      toast({
        title: "Interest submitted!",
        description: "The station owner will be notified of your interest.",
      });
      
      setIsOpen(false);
      (e.target as HTMLFormElement).reset();
    } catch (error) {
      console.error('Error submitting interest:', error);
      toast({
        title: "Error submitting interest",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <Heart className="h-4 w-4" />
          Show Interest
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Express Interest in {stationTitle}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input 
              id="name" 
              name="name" 
              placeholder="Your full name" 
              defaultValue={user?.user_metadata?.full_name || ''}
              required 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input 
              id="email" 
              name="email" 
              type="email" 
              placeholder="your@email.com" 
              defaultValue={user?.email || ''}
              required 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="phone">Phone (Optional)</Label>
            <Input 
              id="phone" 
              name="phone" 
              type="tel" 
              placeholder="+1 (555) 123-4567" 
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Message (Optional)</Label>
            <Textarea 
              id="message" 
              name="message" 
              placeholder="Let them know why you're interested or any specific requirements..."
              className="min-h-[80px]"
            />
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setIsOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? "Submitting..." : "Submit Interest"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};